import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--table"
// Provides client-side sorting, filtering, and row selection for TableComponent
export default class extends Controller {
  static targets = [
    "table",
    "tbody",
    "row",
    "sortHeader",
    "sortIcon",
    "filterRow",
    "filterInput",
    "selectAll",
    "rowCheckbox",
    "selectionCount",
    "visibilityCheckbox",
    "visibilityCountLabel",
    "cellReal",
    "cellSkeleton",
    "loadingStatus",
  ]

  static values = {
    sortable: { type: Boolean, default: false },
    filterable: { type: Boolean, default: false },
    selectable: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: "" },
    tableId: { type: String, default: "" },
  }

  static VISIBILITY_STORAGE_PREFIX = "conek:ui:table:visibility:"

  connect() {
    this._sortColumn = null
    this._sortDirection = null // null -> "asc" -> "desc" -> null
    this._filterDebounceTimer = null
    this._updateSelectionCount()
    this._restoreColumnVisibility()
    // Reconcile the DOM with the fresh controller state on connect so any
    // stale chevron / aria-sort left in the server markup (e.g. a Turbo
    // back-cache restore, or a Lookbook re-render) does not contradict the
    // controller's belief that no column is currently sorted.
    this._updateSortIcons()
  }

  disconnect() {
    if (this._filterDebounceTimer) {
      clearTimeout(this._filterDebounceTimer)
    }
  }

  // --- Loading ---

  // Stimulus calls this on connect and whenever loadingValue changes. It swaps
  // the pre-rendered per-cell layers (cellReal <-> cellSkeleton) by toggling the
  // `hidden` class, so the loading state flips without re-rendering the cells.
  loadingValueChanged(loading, previousLoading) {
    if (this.hasCellRealTarget) {
      this.cellRealTargets.forEach((el) =>
        el.classList.toggle("hidden", loading),
      )
    }
    if (this.hasCellSkeletonTarget) {
      this.cellSkeletonTargets.forEach((el) =>
        el.classList.toggle("hidden", !loading),
      )
    }

    this.element.setAttribute("aria-busy", loading ? "true" : "false")

    // Announce to assistive tech via the polite live region. Setting the text
    // on transition is what triggers the screen-reader announcement (a live
    // region only speaks on content change once it is in the DOM).
    if (this.hasLoadingStatusTarget) {
      this.loadingStatusTarget.textContent = loading
        ? this.loadingTextValue
        : ""
    }

    // Announce real transitions only, not the connect-time initial call
    // (previousLoading is undefined the first time Stimulus invokes this).
    // The connect-time call still reconciles the server-rendered `hidden`
    // classes above, so do not remove the initial forEach to "optimize" — it
    // prevents a flash if hydration lags behind first paint.
    if (previousLoading !== undefined) {
      this._dispatchEvent("table:loading", { loading })
    }
  }

  // --- Sorting ---

  sort(event) {
    if (!this.sortableValue) return

    const header = event.currentTarget
    const key = header.dataset.sortKey

    // Toggle sort direction: none -> asc -> desc -> none
    if (this._sortColumn === key) {
      if (this._sortDirection === "asc") {
        this._sortDirection = "desc"
      } else if (this._sortDirection === "desc") {
        this._sortDirection = null
        this._sortColumn = null
      }
    } else {
      this._sortColumn = key
      this._sortDirection = "asc"
    }

    this._applySorting()
    this._updateSortIcons()
    this._dispatchEvent("table:sort", {
      column: this._sortColumn,
      direction: this._sortDirection,
    })
  }

  _applySorting() {
    if (!this.hasTbodyTarget || !this.hasRowTarget) return

    const rows = Array.from(this.rowTargets)

    if (!this._sortColumn || !this._sortDirection) {
      // Restore original order
      rows.sort((a, b) => {
        return (
          parseInt(a.dataset.rowIndex, 10) - parseInt(b.dataset.rowIndex, 10)
        )
      })
    } else {
      const key = this._sortColumn
      const direction = this._sortDirection

      rows.sort((a, b) => {
        const aCell = a.querySelector(`[data-column-key="${key}"]`)
        const bCell = b.querySelector(`[data-column-key="${key}"]`)
        const aText = this._cellText(aCell)
        const bText = this._cellText(bCell)

        // Try numeric comparison first
        const aNum = parseFloat(aText.replace(/[^0-9.-]/g, ""))
        const bNum = parseFloat(bText.replace(/[^0-9.-]/g, ""))

        let comparison
        if (!isNaN(aNum) && !isNaN(bNum)) {
          comparison = aNum - bNum
        } else {
          comparison = aText.localeCompare(bText)
        }

        return direction === "desc" ? -comparison : comparison
      })
    }

    // Re-append rows in sorted order
    const tbody = this.tbodyTarget
    rows.forEach((row) => tbody.appendChild(row))
  }

  _updateSortIcons() {
    if (!this.hasSortIconTarget) return

    this.sortIconTargets.forEach((iconContainer) => {
      const key = iconContainer.dataset.sortKey
      const svgs = iconContainer.querySelectorAll("svg")
      const upArrow = svgs[0]
      const downArrow = svgs[1]

      if (!upArrow || !downArrow) return

      // NOTE: use classList (never `.className`) on SVG elements — an SVG's
      // `className` is an SVGAnimatedString, not a string, so `.className.replace`
      // throws and aborts the whole loop (including the aria-sort update below).
      const active = key === this._sortColumn && this._sortDirection

      if (active === "asc") {
        // Sorted ascending: show a single clear up arrow, hide the down one.
        this._setArrow(upArrow, { visible: true, active: true })
        this._setArrow(downArrow, { visible: false })
      } else if (active === "desc") {
        // Sorted descending: show a single clear down arrow, hide the up one.
        this._setArrow(upArrow, { visible: false })
        this._setArrow(downArrow, { visible: true, active: true })
      } else {
        // Unsorted: faint double chevron (both visible, muted).
        this._setArrow(upArrow, { visible: true, active: false })
        this._setArrow(downArrow, { visible: true, active: false })
      }

      // The down chevron's `-mt-1` offset only makes sense when it is stacked
      // under the up chevron (the unsorted double-chevron). When a single arrow
      // is shown (sorted), drop it so the lone arrow stays vertically centred.
      downArrow.classList.toggle("-mt-1", !active)
    })

    // Update aria-sort on headers + the button's aria-label so a screen
    // reader user tabbing linearly to the button hears the current sort
    // state on the focused element (not just on the ancestor <th>, which
    // some ATs don't surface in linear-tab mode).
    if (this.hasSortHeaderTarget) {
      this.sortHeaderTargets.forEach((header) => {
        const key = header.dataset.sortKey
        const state =
          key === this._sortColumn && this._sortDirection
            ? this._sortDirection === "asc"
              ? "ascending"
              : "descending"
            : "none"
        header.setAttribute("aria-sort", state)

        const button = header.querySelector("button")
        if (button) {
          const labelKey =
            state === "ascending"
              ? "sortLabelAscending"
              : state === "descending"
                ? "sortLabelDescending"
                : "sortLabelNone"
          const nextLabel = button.dataset[labelKey]
          if (nextLabel) button.setAttribute("aria-label", nextLabel)
        }
      })
    }
  }

  // Toggle a single sort chevron's visibility and emphasis. `active` renders it
  // in the foreground colour (the current sort direction); otherwise it stays
  // muted. Hidden chevrons are removed so a sorted column shows ONE clear arrow.
  _setArrow(arrow, { visible, active = false }) {
    arrow.classList.toggle("hidden", !visible)
    arrow.classList.toggle("text-foreground", active)
    arrow.classList.toggle("text-muted-foreground/40", !active)
  }

  // --- Filtering ---

  filter() {
    if (!this.filterableValue) return

    // Debounce filter input
    if (this._filterDebounceTimer) {
      clearTimeout(this._filterDebounceTimer)
    }

    this._filterDebounceTimer = setTimeout(() => {
      this._applyFiltering()
    }, 300)
  }

  _applyFiltering() {
    if (!this.hasRowTarget || !this.hasFilterInputTarget) return

    // Gather filter values
    const filters = {}
    this.filterInputTargets.forEach((input) => {
      const key = input.dataset.filterKey
      const value = input.value.trim().toLowerCase()
      if (value) {
        filters[key] = value
      }
    })

    // Show/hide rows based on filters
    this.rowTargets.forEach((row) => {
      let visible = true

      for (const [key, filterValue] of Object.entries(filters)) {
        const cell = row.querySelector(`[data-column-key="${key}"]`)
        if (cell) {
          const cellText = this._cellText(cell)
          if (!cellText.includes(filterValue)) {
            visible = false
            break
          }
        }
      }

      row.style.display = visible ? "" : "none"
    })

    this._dispatchEvent("table:filter", { filters })
  }

  // --- Selection ---

  toggleAll(event) {
    if (!this.selectableValue) return

    const checked = event.currentTarget.checked

    this.rowCheckboxTargets.forEach((checkbox) => {
      // Only toggle visible rows (not hidden by filter). A logical row may have
      // two checkboxes (desktop <tr> + mobile card), so resolve both container
      // shapes — otherwise "select all" would skip the mobile-card checkboxes.
      const row = this._rowContainerFor(checkbox)
      if (row && row.style.display !== "none") {
        checkbox.checked = checked
      }
    })

    this._syncSelectedStates()
    this._updateSelectionCount()
    this._dispatchEvent("table:select", { selectedIds: this._getSelectedIds() })
  }

  toggleRow(event) {
    if (!this.selectableValue) return

    // A logical row can be represented by two checkboxes sharing a value
    // (desktop <tr> + responsive mobile card). Keep the sibling in sync so both
    // viewports agree and the derived selection state is consistent.
    if (event?.target) this._syncSiblingCheckboxes(event.target)

    this._syncSelectedStates()
    this._updateSelectAllState()
    this._updateSelectionCount()
    this._dispatchEvent("table:select", { selectedIds: this._getSelectedIds() })
  }

  // Resolve the element that carries a checkbox's selection styling. Desktop
  // rows are <tr>; the responsive mobile layout renders cards instead
  // (data-selectable-card), so cover both.
  _rowContainerFor(checkbox) {
    return (
      checkbox.closest("tr") ||
      checkbox.closest("[data-ui--table-target='row']") ||
      checkbox.closest("[data-selectable-card]")
    )
  }

  // Propagate a checkbox's checked state to any other checkbox for the same
  // logical row (same value), so desktop and mobile stay in lockstep.
  _syncSiblingCheckboxes(source) {
    this.rowCheckboxTargets.forEach((cb) => {
      if (cb !== source && cb.value === source.value) {
        cb.checked = source.checked
      }
    })
  }

  // Mirror each row checkbox's checked state onto its container's data-state so
  // the selected styling (data-[state=selected]:bg-muted) updates live, matching
  // @a-safe-digital/react-table's data-state={row.getIsSelected() && "selected"}.
  _syncSelectedStates() {
    if (!this.hasRowCheckboxTarget) return

    this.rowCheckboxTargets.forEach((checkbox) => {
      const row = this._rowContainerFor(checkbox)
      if (!row) return

      if (checkbox.checked) {
        row.setAttribute("data-state", "selected")
      } else {
        row.removeAttribute("data-state")
      }
    })
  }

  _updateSelectAllState() {
    if (!this.hasSelectAllTarget || !this.hasRowCheckboxTarget) return

    // De-dupe by value: one logical row may expose two checkboxes (desktop +
    // mobile), and a row counts as checked if any of its checkboxes is checked.
    const checkedByValue = new Map()
    this.rowCheckboxTargets.forEach((cb) => {
      const row = this._rowContainerFor(cb)
      if (row && row.style.display === "none") return
      checkedByValue.set(
        cb.value,
        checkedByValue.get(cb.value) || false || cb.checked,
      )
    })

    const totalCount = checkedByValue.size
    const checkedCount = [...checkedByValue.values()].filter(Boolean).length

    this.selectAllTarget.checked = totalCount > 0 && checkedCount === totalCount
    this.selectAllTarget.indeterminate =
      checkedCount > 0 && checkedCount < totalCount
  }

  _updateSelectionCount() {
    if (!this.hasSelectionCountTarget) return

    const count = this._getSelectedIds().length
    this.selectionCountTarget.textContent = count.toString()
  }

  _getSelectedIds() {
    if (!this.hasRowCheckboxTarget) return []

    // De-dupe: a logical row may have two checkboxes (desktop + mobile) sharing
    // a value, so a selected item must be reported once.
    return [
      ...new Set(
        this.rowCheckboxTargets
          .filter((cb) => cb.checked)
          .map((cb) => cb.value),
      ),
    ]
  }

  // --- Column visibility ---

  toggleColumnVisibility(event) {
    const checkbox = event.currentTarget
    const key = checkbox.dataset.columnKey
    if (!key) return

    const visible = checkbox.checked
    this._applyColumnVisibility(key, visible)
    this._syncAriaChecked(checkbox, visible)
    this._updateVisibilityCount()
    this._persistColumnVisibility()
    this._dispatchEvent("table:columnVisibility", { column: key, visible })
  }

  _syncAriaChecked(checkbox, checked) {
    const menuItem = checkbox.closest('[role="menuitemcheckbox"]')
    if (menuItem) {
      menuItem.setAttribute("aria-checked", checked ? "true" : "false")
    }
  }

  _updateVisibilityCount() {
    if (
      !this.hasVisibilityCheckboxTarget ||
      !this.hasVisibilityCountLabelTarget
    )
      return

    const visibleCount = this.visibilityCheckboxTargets.filter(
      (cb) => cb.checked,
    ).length
    const text = visibleCount > 0 ? ` (${visibleCount})` : ""

    this.visibilityCountLabelTargets.forEach((el) => {
      el.textContent = text
    })
  }

  _applyColumnVisibility(key, visible) {
    const safeKey = window.CSS && CSS.escape ? CSS.escape(key) : key
    const elements = this.element.querySelectorAll(
      `[data-column-key="${safeKey}"]`,
    )
    elements.forEach((el) => {
      // Never hide the visibility-toggle checkbox itself (it lives inside the
      // dropdown with the same data-column-key attribute).
      if (
        el.matches(
          'input[type="checkbox"][data-action*="toggleColumnVisibility"]',
        )
      ) {
        return
      }
      el.style.display = visible ? "" : "none"
    })
  }

  _storageKey() {
    const id = this.tableIdValue
    if (!id) return null
    return `${this.constructor.VISIBILITY_STORAGE_PREFIX}${id}`
  }

  _readVisibilityState() {
    const key = this._storageKey()
    if (!key || typeof window === "undefined" || !window.localStorage)
      return null

    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === "object" ? parsed : null
    } catch {
      return null
    }
  }

  _restoreColumnVisibility() {
    const state = this._readVisibilityState()
    if (!state) return

    // Skip persisted keys that no longer have a corresponding checkbox in the
    // dropdown (e.g. a column changed from hideable: true to hideable: false
    // after the state was saved). Otherwise a stale entry could keep a column
    // hidden with no UI to bring it back.
    const knownKeys = this.hasVisibilityCheckboxTarget
      ? new Set(
          this.visibilityCheckboxTargets.map((cb) => cb.dataset.columnKey),
        )
      : new Set()

    Object.entries(state).forEach(([key, visible]) => {
      if (!knownKeys.has(key)) return

      const visibleBool = Boolean(visible)
      this._applyColumnVisibility(key, visibleBool)
      const checkbox = this.visibilityCheckboxTargets.find(
        (cb) => cb.dataset.columnKey === key,
      )
      if (checkbox) {
        checkbox.checked = visibleBool
        this._syncAriaChecked(checkbox, visibleBool)
      }
    })

    this._updateVisibilityCount()
  }

  _persistColumnVisibility() {
    const key = this._storageKey()
    if (!key || typeof window === "undefined" || !window.localStorage) return
    if (!this.hasVisibilityCheckboxTarget) return

    const state = {}
    this.visibilityCheckboxTargets.forEach((cb) => {
      const columnKey = cb.dataset.columnKey
      if (columnKey) state[columnKey] = cb.checked
    })

    try {
      window.localStorage.setItem(key, JSON.stringify(state))
    } catch {
      // Best-effort persistence: swallow quota / availability errors.
    }
  }

  // --- Utilities ---

  // Read a cell's real (non-skeleton) text. Every body cell carries both a
  // `cellReal` and a `cellSkeleton` layer; the skeleton stays in the DOM even
  // when hidden, and `textContent` includes display:none descendants — so a
  // custom, text-bearing skeleton would otherwise contaminate sort keys and
  // filter matches. Scope the read to the real layer, falling back to the cell.
  _cellText(cell) {
    if (!cell) return ""
    const real = cell.querySelector("[data-ui--table-target='cellReal']")
    return (real || cell).textContent.trim().toLowerCase()
  }

  _dispatchEvent(name, detail) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }))
  }
}
